/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { ENDPOINTS, parseApiError, restClient } from '../../../../../../interface/api';
import { ALREADY_RUNNING_REASON } from './repositoryReevaluation.constants';
import type { ReevaluationStatus, StartOutcome } from './repositoryReevaluation.types';
import { EVALUATIONS_STRINGS as STRINGS } from './evaluations.strings';

/** Failure of a repository re-evaluation request; the message is the server's sanitized text or a fixed fallback. */
export class RepositoryReevaluationApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RepositoryReevaluationApiError';
  }
}

interface ErrorResponse {
  status?: unknown;
  data?: { reason?: unknown; requestId?: unknown };
}

const responseOf = (error: unknown): ErrorResponse | undefined =>
  (error as { response?: ErrorResponse } | null | undefined)?.response;

const evaluateUrl = (repositoryId: string) =>
  `${ENDPOINTS.FIREWALL_EVALUATIONS}/repositories/${encodeURIComponent(repositoryId)}/evaluate`;

const toApiError = (error: unknown, fallback: string): RepositoryReevaluationApiError =>
  new RepositoryReevaluationApiError(parseApiError(error).message || fallback);

/** A 409 with the already-running reason is an answer; any other failure, including another 409, is thrown. */
export async function startRepositoryReevaluation(repositoryId: string): Promise<StartOutcome> {
  try {
    const started = await restClient.post<{ requestId: string }>(evaluateUrl(repositoryId));
    return { kind: 'started', requestId: started.requestId };
  } catch (error) {
    const response = responseOf(error);
    if (response?.status === 409 && response.data?.reason === ALREADY_RUNNING_REASON) {
      const requestId = response.data.requestId;
      return { kind: 'alreadyRunning', requestId: typeof requestId === 'string' ? requestId : null };
    }
    throw toApiError(error, STRINGS.reevaluate.errors.start);
  }
}

export async function getRepositoryReevaluationStatus(
  repositoryId: string,
  requestId: string
): Promise<ReevaluationStatus> {
  try {
    const answer = await restClient.get<{ status: ReevaluationStatus }>(
      `${evaluateUrl(repositoryId)}/status/${encodeURIComponent(requestId)}`
    );
    return answer.status;
  } catch (error) {
    throw toApiError(error, STRINGS.reevaluate.errors.poll);
  }
}
